import type { ActivityFeedRowDTO } from '../../../services/modules/activityFeed/activityFeed.types';
import { DATE_STRING_FORMAT, formatDate } from '../../../utils/dates';
import {
  ActivityFeedDayGroup,
  ActivityFeedShipmentIndex,
  ActivityFeedShipmentRow,
  GroupItemsByDayParams,
} from './activityFeedGrouping.types';
import { findShipmentStatusChip } from './activityFeedShipment.constants';

const getDayKey = (date: Date, timeZone?: string) => formatDate(date, DATE_STRING_FORMAT, timeZone);

export const groupItemsByDay = <Item>({
  items,
  getDate,
  timeZone,
}: GroupItemsByDayParams<Item>): ActivityFeedDayGroup<Item>[] => {
  const groups: ActivityFeedDayGroup<Item>[] = [];
  let currentKey: string | undefined;

  for (const item of items) {
    const dayKey = getDayKey(new Date(getDate(item)), timeZone);

    if (!dayKey) {
      continue;
    }

    const currentGroup = groups[groups.length - 1];

    if (currentGroup && dayKey === currentKey) {
      currentGroup.items.push(item);
      continue;
    }

    currentKey = dayKey;
    groups.push({ dayKey, items: [item] });
  }

  return groups;
};

export const getDayGroupCounts = <Item>(groups: ActivityFeedDayGroup<Item>[]): number[] =>
  groups.map((group) => group.items.length);

const isShipmentRow = (row: ActivityFeedRowDTO): row is ActivityFeedShipmentRow =>
  row.eventType === 'order.shipment.created' || row.eventType === 'order.shipment.updated';

export const getShipmentKey = ({ payload }: ActivityFeedShipmentRow) =>
  `${payload.orderCode}::${payload.shipmentExternalCode}`;

const hasRenderableStatus = ({ payload }: ActivityFeedShipmentRow) =>
  Boolean(findShipmentStatusChip(payload.status));

const keepLatestShipmentRow = (
  index: ActivityFeedShipmentIndex,
  row: ActivityFeedShipmentRow,
): ActivityFeedShipmentIndex => {
  const key = getShipmentKey(row);
  const latest = index[key];

  if (!latest || new Date(row.occurredAt) > new Date(latest.latestOccurredAt)) {
    index[key] = {
      latestRowId: row.id,
      latestStatus: row.payload.status,
      latestOccurredAt: row.occurredAt,
      shippingCompanyName: row.payload.shippingCompanyName,
    };
  }

  return index;
};

export const indexRowsByShipment = (rows: ActivityFeedRowDTO[]): ActivityFeedShipmentIndex =>
  rows.filter(isShipmentRow).filter(hasRenderableStatus).reduce(keepLatestShipmentRow, {});
